import { hotSearch, liveFeed } from "@/mocks/hotSearch";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import { formatHeat, heatToPercent } from "@/utils/format";

const TREND_ICON: Record<string, { icon: string; className: string }> = {
  up: { icon: "ri-arrow-up-line", className: "text-primary-500" },
  down: { icon: "ri-arrow-down-line", className: "text-secondary-500" },
  new: { icon: "ri-add-line", className: "text-accent-600" },
  same: { icon: "ri-subtract-line", className: "text-foreground-400" },
};

const TAG_STYLE: Record<string, string> = {
  爆: "bg-primary-500 text-background-50",
  热: "bg-accent-500 text-foreground-950",
  新: "bg-secondary-500 text-background-50",
};

export default function HotRankingSection() {
  const maxHeat = Math.max(...hotSearch.map((item) => item.heat));

  return (
    <section id="ranking" className="scroll-mt-20 bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <Reveal>
        <SectionHeading
          eyebrow="Real-time Ranking"
          title="实时热搜榜，热度一目了然"
          description="榜单每几分钟刷新一次，综合讨论量、阅读量与增速计算热度。排名越靠前，说明此刻正在被越多的人围观。"
        />
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <Reveal className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-2 md:p-3">
            <ol className="divide-y divide-background-200/80">
              {hotSearch.map((item) => {
                const trend = TREND_ICON[item.trend];
                const isTopThree = item.rank <= 3;
                return (
                  <li key={item.title}>
                    <a
                      href="#topics"
                      className="group flex items-center gap-3 rounded-md px-2 py-3 transition-colors hover:bg-background-50 md:gap-4 md:px-3 md:py-4"
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold md:h-9 md:w-9 ${
                          isTopThree
                            ? item.rank === 1
                              ? "bg-primary-500 text-background-50"
                              : item.rank === 2
                                ? "bg-primary-700 text-background-50"
                                : "bg-accent-500 text-foreground-950"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {item.rank}
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600 md:text-base">
                            {item.title}
                          </span>
                          {item.tag ? (
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${TAG_STYLE[item.tag]}`}
                            >
                              {item.tag}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                          <span
                            className="block h-full animate-bar-grow rounded-full bg-gradient-to-r from-accent-400 to-primary-500"
                            style={{ width: `${heatToPercent(item.heat, maxHeat)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex shrink-0 flex-col items-end">
                        <span className="font-heading text-sm font-bold text-foreground-800">
                          {formatHeat(item.heat)}
                        </span>
                        <span className={`flex items-center text-xs ${trend.className}`}>
                          <i className={trend.icon} aria-hidden="true" />
                        </span>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <aside className="flex h-full flex-col rounded-lg border border-background-200 bg-secondary-50 p-5 md:p-6">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-chat-smile-2-line" aria-hidden="true" />
                </span>
                围观现场
              </h3>
              <span className="flex items-center gap-1.5 text-xs text-secondary-700">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-live-pulse rounded-full bg-secondary-500" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary-500" />
                </span>
                实时
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              大家正在评论区里聊什么，滚动即可看到最新讨论。
            </p>

            <ul className="mt-5 flex-1 space-y-4">
              {liveFeed.map((item) => (
                <li
                  key={`${item.user}-${item.time}`}
                  className="rounded-md border border-secondary-200/80 bg-background-50 p-3.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 font-heading text-xs font-bold text-accent-900">
                        {item.user.slice(0, 1)}
                      </span>
                      <span className="text-xs font-medium text-foreground-800">{item.user}</span>
                    </div>
                    <span className="shrink-0 text-[11px] text-foreground-400">{item.time}</span>
                  </div>
                  <p className="mt-2.5 text-xs leading-relaxed text-foreground-700">
                    {item.content}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-foreground-500">
                    <span className="truncate rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-800">
                      #{item.topic}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-primary-500">
                      <i className="ri-heart-3-line" aria-hidden="true" />
                      {item.likes}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <a
              href="#topics"
              className="mt-5 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
            >
              进入话题现场
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </a>
          </aside>
        </Reveal>
      </div>
    </section>
  );
}